import { useCallback, useRef, useState } from 'react';
import type {
  FacilityFlowCoreModulesFleetDtosEndVehicleTripRequest as EndRequest,
  FacilityFlowCoreModulesFleetDtosStartVehicleTripRequest as StartRequest,
} from '@/api/generated/models';
import type { CurrentTrip } from '@/store/useMyVehicleTripsStore';
import type { VehicleDto } from '@/store/useVehicleStore';
import type { TripChoice, TripDialogMode } from '@/utils/vehicleTrips';

/** `clockOut` offers "Ausstempeln und Fahrt beenden" or "Nur ausstempeln"; `end` only ends the trip. */
export type EndTripMode = 'clockOut' | 'end';

/** Start answer: the trip to start, or null for "Kein Fahrer". */
export interface TripStartAnswer {
  trip: StartRequest | null;
}

/** End answer: the trip end, or null for "Nur ausstempeln, Fahrt läuft weiter". */
export interface TripEndAnswer {
  endTrip: EndRequest | null;
}

export interface TripStartDialogState {
  isOpen: boolean;
  mode: TripDialogMode;
  vehicles: VehicleDto[];
  initial: TripChoice | null;
  /** When the trip departs (ISO; now without) — what start, destination and purpose are suggested for (FAFD-68). */
  departureAt?: string;
  /** The employee is not clocked in — starting is allowed, with a hint. */
  notClockedIn: boolean;
  onConfirm: (answer: TripStartAnswer) => void;
  onCancel: () => void;
}

export interface EndTripDialogState {
  isOpen: boolean;
  mode: EndTripMode;
  trip: CurrentTrip | null;
  onConfirm: (answer: TripEndAnswer) => void;
  onCancel: () => void;
}

interface StartRequestInput {
  mode: TripDialogMode;
  vehicles: VehicleDto[];
  initial: TripChoice;
  departureAt?: string;
  notClockedIn: boolean;
}

/**
 * The two trip questions (FAFD-68) as promises: `askStart` opens TripStartDialog, `askEnd` opens
 * EndTripDialog; each resolves with the answer, or null when the employee cancelled. Used by the
 * clock stamps (useVehicleStamp) and by "Fahrt starten / beenden" (TripActions).
 */
export function useTripDialogs() {
  const [start, setStart] = useState<StartRequestInput | null>(null);
  const [end, setEnd] = useState<{ mode: EndTripMode; trip: CurrentTrip } | null>(null);
  const settleStart = useRef<((answer: TripStartAnswer | null) => void) | null>(null);
  const settleEnd = useRef<((answer: TripEndAnswer | null) => void) | null>(null);

  const askStart = useCallback(
    (input: StartRequestInput) =>
      new Promise<TripStartAnswer | null>((resolve) => {
        settleStart.current = resolve;
        setStart(input);
      }),
    [],
  );

  const askEnd = useCallback(
    (mode: EndTripMode, trip: CurrentTrip) =>
      new Promise<TripEndAnswer | null>((resolve) => {
        settleEnd.current = resolve;
        setEnd({ mode, trip });
      }),
    [],
  );

  const finishStart = useCallback((answer: TripStartAnswer | null) => {
    const resolve = settleStart.current;
    settleStart.current = null;
    setStart(null);
    resolve?.(answer);
  }, []);

  const finishEnd = useCallback((answer: TripEndAnswer | null) => {
    const resolve = settleEnd.current;
    settleEnd.current = null;
    setEnd(null);
    resolve?.(answer);
  }, []);

  const startDialog: TripStartDialogState = {
    isOpen: start !== null,
    mode: start?.mode ?? 'start',
    vehicles: start?.vehicles ?? [],
    initial: start?.initial ?? null,
    departureAt: start?.departureAt,
    notClockedIn: start?.notClockedIn ?? false,
    onConfirm: finishStart,
    onCancel: () => finishStart(null),
  };

  const endDialog: EndTripDialogState = {
    isOpen: end !== null,
    mode: end?.mode ?? 'end',
    trip: end?.trip ?? null,
    onConfirm: finishEnd,
    onCancel: () => finishEnd(null),
  };

  return { askStart, askEnd, startDialog, endDialog };
}
